import { RefObject, useRef, useState } from "react";

import {
  UseMutateAsyncFunction,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { produce } from "immer";

import {
  featureQueriesApi as featureQueriesApiCreator,
  featuresApi as featuresApiCreator,
  retrieveFeaturesApi,
} from "src/api/endpoints";
import {
  BackfillQueryRequestEnvironmentEnum,
  FeatureQuery,
  FeatureQueryCreate,
  FeatureQueryPut,
  FeatureResponse,
  PreviewQueryRequestEnvironmentEnum,
  PreviewQueryResponse,
  PreviewQueryTriggerResponse,
  SetFeatureQueryStatusRequest,
} from "src/clients/features-control";
import { GetFeatureByEntityFeaturesEnvironmentEntityTypeEntityIdGetEnvironmentEnum } from "src/clients/features-retrieve/api";
import { FeatureForm } from "src/featureCatalogue/Features/ManageFeatureModal";
import { BackfillQueryForm } from "src/featureCatalogue/Queries/BackfillQueryModal";
import { Environment } from "src/hooks/useEnvironment";
import { queryClient } from "src/queryClient";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";

const PREFIX_CACHE_KEY = "features";

type UseFeaturesParams = {
  environment?: Environment;
  entityType?: string;
  options?: Pick<
    UseQueryOptions<FeatureResponse[], AxiosError<{ detail: string }>>,
    "enabled" | "select"
  >;
};

export const useFeatures = ({
  environment,
  entityType,
  options,
}: UseFeaturesParams = {}) => {
  const { workspace } = useWorkspaceContext();
  const featuresApi = featuresApiCreator(workspace.base_url!);

  return useQuery({
    queryKey: [PREFIX_CACHE_KEY, workspace.id, { environment, entityType }],
    queryFn: async () =>
      (
        await featuresApi.getFeaturesFeaturesGet(
          entityType ? { entityTypes: entityType } : undefined,
        )
      ).data,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

const selectEntityRelatedFeatures = (features: FeatureResponse[]) => {
  return features.filter((feature) => feature.entity_types?.length !== 0);
};

export const useEntityRelatedFeatures = (options: UseFeaturesParams) => {
  return useFeatures({
    options: {
      select: selectEntityRelatedFeatures,
      ...options,
    },
  });
};

export const useCreateFeature = () => {
  const { workspace } = useWorkspaceContext();
  const featuresApi = featuresApiCreator(workspace.base_url!);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (feature: FeatureForm) =>
      featuresApi.createFeatureHandlerFeaturesPost({
        featureCreate: {
          key: feature.key,
          name: feature.name,
          description: feature.description,
          query_id: null,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PREFIX_CACHE_KEY] });
    },
  });
};

export const useArchiveFeatureQuery = () => {
  const { workspace } = useWorkspaceContext();
  const featureQueriesApi = featureQueriesApiCreator(workspace.base_url!);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, etag }: { id: string; etag: string }) =>
      featureQueriesApi.archiveFeatureQueryHandlerFeatureQueriesIdArchivePost({
        id,
        ifMatch: etag,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FEATURE_QUERIES_PREFIX_CACHE_KEY, workspace.id],
      });
      queryClient.invalidateQueries({
        queryKey: [PREFIX_CACHE_KEY, workspace.id],
      });
    },
  });
};

export const useUpdateFeature = () => {
  const { workspace } = useWorkspaceContext();
  const featuresApi = featuresApiCreator(workspace.base_url!);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      feature,
      etag,
    }: {
      key: string;
      feature: Partial<Omit<FeatureForm, "key">>;
      etag: string;
    }) =>
      featuresApi.patchFeatureHandlerFeaturesKeyPatch({
        key,
        featurePatch: {
          name: feature.name,
          description: feature.description,
        },
        ifMatch: etag,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PREFIX_CACHE_KEY] });
    },
  });
};

const FEATURE_QUERIES_PREFIX_CACHE_KEY = "feature-queries";

export const useFeatureQueries = () => {
  const { workspace } = useWorkspaceContext();
  const featureQueriesApi = featureQueriesApiCreator(workspace.base_url!);

  return useQuery({
    queryKey: [FEATURE_QUERIES_PREFIX_CACHE_KEY, workspace.id],
    queryFn: async () =>
      (await featureQueriesApi.getFeatureQueriesFeatureQueriesGet()).data,
  });
};

export const useFeatureQuery = (id: string) => {
  const { workspace } = useWorkspaceContext();
  const featureQueriesApi = featureQueriesApiCreator(workspace.base_url!);
  return useQuery({
    queryKey: [FEATURE_QUERIES_PREFIX_CACHE_KEY, workspace.id, id],
    queryFn: async () =>
      (
        await featureQueriesApi.getFeatureQueryFeatureQueriesIdGet({
          id,
        })
      ).data,
    enabled: !!id,
  });
};

export type UseFeatureQueryPreviewMutationArgs = {
  queryString: string;
  windowLength: number;
  environment: PreviewQueryRequestEnvironmentEnum;
  endTime: Date | undefined;
};
export const useFeatureQueryPreviewMutation = (
  onSuccess:
    | ((
        data: PreviewQueryTriggerResponse,
        variables: UseFeatureQueryPreviewMutationArgs,
        context: unknown,
      ) => Promise<unknown> | unknown)
    | undefined,
) => {
  const { workspace } = useWorkspaceContext();
  const featureQueriesApi = featureQueriesApiCreator(workspace.base_url!);

  return useMutation({
    mutationKey: [FEATURE_QUERIES_PREFIX_CACHE_KEY, workspace.id, "preview"],
    mutationFn: async ({
      queryString,
      environment,
      windowLength = 300,
      endTime,
    }: UseFeatureQueryPreviewMutationArgs) => {
      return (
        await featureQueriesApi.previewQueryFeatureQueriesPreviewPost({
          previewQueryRequest: {
            query: queryString,
            limit: 100,
            window_length: windowLength,
            environment,
            end_time: endTime?.toISOString(),
          },
        })
      ).data;
    },
    onSuccess: onSuccess,
    onError: (error, variables) => {
      if ((error as AxiosError).response?.status === 500) {
        logger.error(
          "Unexpected error: Feature query preview failed: ",
          error,
          "Query: ",
          variables,
        );
      }
    },
    throwOnError: false,
  });
};
const getFeatureQueryPreviewResponseKey = (
  baseUrl: string,
  executionToken: string,
) => ["featureQueryPreviewResponse", baseUrl, executionToken];

const useFeatureQueryPreviewResponse = (
  executionToken: string | undefined,
  promise: RefObject<CustomPromise | null>,
) => {
  const { workspace } = useWorkspaceContext();
  const featureQueriesApi = featureQueriesApiCreator(workspace.base_url!);
  return useQuery({
    queryKey: getFeatureQueryPreviewResponseKey(
      workspace.base_url!,
      executionToken!,
    ),
    queryFn: async () => {
      try {
        const response =
          await featureQueriesApi.getPreviewQueryFeatureQueriesPreviewExecutionTokenGet(
            { executionToken: executionToken! },
          );

        if (response.status === 200 && response.data) {
          promise.current?.resolve(response.data);
        } else if (response.status !== 202) {
          promise.current?.reject();
        }

        return response;
      } catch (error) {
        logger.error("Error fetching feature query preview response", error);
        promise.current?.reject();
        throw error;
      }
    },
    select: (data) => data.data,
    refetchInterval: (data) => {
      return data?.state.data?.status === 202 && !data?.state.error
        ? 2000
        : false;
    },
    enabled: !!executionToken && !!workspace.base_url,
  });
};

export type UseFeatureQueryPreview = {
  triggerPreview: UseMutateAsyncFunction<
    PreviewQueryTriggerResponse,
    Error,
    UseFeatureQueryPreviewMutationArgs,
    unknown
  >;
  previewData: PreviewQueryResponse | undefined;
  isPending: boolean;
  reset: () => void;
  error: AxiosError<{ detail?: string }> | null;
  isIdle: boolean;
  variables: UseFeatureQueryPreviewMutationArgs | undefined;
  isSuccess: boolean;
  promise: RefObject<CustomPromise | null>;
};

type CustomPromise = {
  reject: () => void;
  resolve: (data: PreviewQueryResponse) => void;
  promise: Promise<PreviewQueryResponse>;
};

const buildCustomPromise = () => {
  let resolveFn, rejectFn;
  const promise = new Promise<PreviewQueryResponse>((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });

  return {
    reject: rejectFn as unknown as () => void,
    resolve: resolveFn as unknown as (data: PreviewQueryResponse) => void,
    promise,
  };
};

export const useFeatureQueryPreview = (): UseFeatureQueryPreview => {
  const [executionToken, setExecutionToken] = useState<string>();
  const { workspace } = useWorkspaceContext();
  const promiseRef = useRef<CustomPromise | null>(null);

  const previewMutation = useFeatureQueryPreviewMutation((data) => {
    queryClient.invalidateQueries({
      queryKey: getFeatureQueryPreviewResponseKey(
        workspace.base_url!,
        data.execution_token,
      ),
    });
    setExecutionToken(data.execution_token);
  });
  const previewResponse = useFeatureQueryPreviewResponse(
    executionToken,
    promiseRef,
  );

  return {
    triggerPreview: (...args) => {
      promiseRef.current = buildCustomPromise();
      return previewMutation.mutateAsync(...args);
    },
    promise: promiseRef,
    previewData: Array.isArray(previewResponse.data?.data)
      ? previewResponse.data
      : undefined,
    isPending:
      previewMutation.isPending ||
      previewResponse.isFetching ||
      (Object.hasOwn(previewResponse.data ?? {}, "detail") &&
        !previewResponse.error),
    reset: () => {
      previewMutation.reset();
      setExecutionToken(undefined);
      promiseRef.current = null;
    },
    error: (previewMutation.error || previewResponse.error) as AxiosError<{
      detail?: string;
    }> | null,
    isIdle: previewMutation.isIdle,
    variables: previewMutation.variables,
    isSuccess: previewMutation.isSuccess,
  };
};

export const useExploreEventTable = (eventType: string | null) => {
  const { workspace } = useWorkspaceContext();
  const featureQueriesApi = featureQueriesApiCreator(workspace.base_url!);

  return useQuery({
    queryKey: [
      FEATURE_QUERIES_PREFIX_CACHE_KEY,
      workspace.id,
      eventType,
      "explore",
    ],
    queryFn: async () =>
      (
        await featureQueriesApi.exploreQueryFeatureQueriesExplorePost({
          exploreFeatureQueryRequest: {
            event: eventType!,
          },
        })
      ).data.data.at(0),
    enabled: !!eventType,
  });
};

export const useCreateFeatureQuery = ({
  invalidateFeatures = false,
}: {
  invalidateFeatures?: boolean;
} = {}) => {
  const { workspace } = useWorkspaceContext();
  const featureQueriesApi = featureQueriesApiCreator(workspace.base_url!);
  const queryClient = useQueryClient();

  return useMutation<
    FeatureQuery,
    AxiosError<{ detail: string }>,
    FeatureQueryCreate
  >({
    mutationFn: async (query: FeatureQueryCreate) => {
      return (
        await featureQueriesApi.createFeatureQueryHandlerFeatureQueriesPost({
          featureQueryCreate: query,
        })
      ).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FEATURE_QUERIES_PREFIX_CACHE_KEY, workspace.id],
      });
      if (invalidateFeatures) {
        // Invalidate features so the Features table reflects newly created features.
        queryClient.invalidateQueries({
          queryKey: [PREFIX_CACHE_KEY, workspace.id],
        });
      }
    },
  });
};

type UseUpdateFeatureQueryProps = {
  onError?: (error: AxiosError<{ detail: string }>) => void;
  invalidateFeatures?: boolean;
};

export const useUpdateFeatureQuery = ({
  onError,
  invalidateFeatures = false,
}: UseUpdateFeatureQueryProps = {}) => {
  const { workspace } = useWorkspaceContext();
  const featureQueriesApi = featureQueriesApiCreator(workspace.base_url!);
  const queryClient = useQueryClient();

  return useMutation<
    FeatureQuery,
    AxiosError<{ detail: string }>,
    FeatureQueryPut & { id: string; etag: string }
  >({
    mutationFn: async ({ id, etag, ...query }) => {
      return (
        await featureQueriesApi.updateFeatureQueryHandlerFeatureQueriesIdPut({
          id,
          featureQueryPut: query,
          ifMatch: etag,
        })
      ).data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FEATURE_QUERIES_PREFIX_CACHE_KEY, workspace.id],
      });
      if (invalidateFeatures) {
        queryClient.invalidateQueries({
          queryKey: [PREFIX_CACHE_KEY, workspace.id],
        });
      }
    },
    onError,
  });
};

export const useSetFeatureQueryStatus = () => {
  const { workspace } = useWorkspaceContext();
  const featureQueriesApi = featureQueriesApiCreator(workspace.base_url!);
  const queryClient = useQueryClient();

  return useMutation<
    FeatureQuery,
    AxiosError<{ detail: string }>,
    SetFeatureQueryStatusRequest & { id: string; etag: string },
    { prevFeatureQueries: FeatureQuery[] | undefined }
  >({
    mutationFn: async ({ id, etag, ...query }) => {
      return (
        await featureQueriesApi.setFeatureQueryStatusHandlerFeatureQueriesIdSetStatusPost(
          {
            id,
            setFeatureQueryStatusRequest: query,
            ifMatch: etag,
          },
        )
      ).data;
    },
    onMutate: async ({ id, status, environment }) => {
      const featureQueriesKey = [
        FEATURE_QUERIES_PREFIX_CACHE_KEY,
        workspace.id,
      ];
      // Cancel the query to prevent race condition
      await queryClient.cancelQueries({
        queryKey: featureQueriesKey,
      });

      // Get the previous data
      const prevFeatureQueries =
        queryClient.getQueryData<FeatureQuery[]>(featureQueriesKey);

      // Set the optimistically updated data
      queryClient.setQueryData(
        featureQueriesKey,
        produce(prevFeatureQueries, (draft) => {
          if (draft) {
            const featureQueryToUpdate = draft.find((c) => c.id === id);

            if (featureQueryToUpdate) {
              if (!featureQueryToUpdate.environment_settings) {
                featureQueryToUpdate.environment_settings = {};
              }

              featureQueryToUpdate.environment_settings[environment].status =
                status;
            }
          }
        }),
      );

      // Return the previous data to be used in the onError callback
      return { prevFeatureQueries };
    },
    onError: (error, variables, context) => {
      // Rollback the optimistic update if the mutation fails
      queryClient.setQueryData(
        [FEATURE_QUERIES_PREFIX_CACHE_KEY, workspace.id],
        context?.prevFeatureQueries,
      );
    },
    onSettled: () => {
      // Invalidate the query to reflect the latest data
      queryClient.invalidateQueries({
        queryKey: [FEATURE_QUERIES_PREFIX_CACHE_KEY, workspace.id],
      });
      // Also invalidate features so the Features table reflects the status change
      queryClient.invalidateQueries({
        queryKey: [PREFIX_CACHE_KEY, workspace.id],
      });
    },
  });
};

export const featureQueryKeys = {
  entityTypeFeatures: (
    workspaceId: string,
    environment: Environment,
    entityType: string,
  ) => ["features", workspaceId, environment, entityType],
  entityFeatures: (
    workspaceId: string,
    environment: Environment,
    entityType: string,
    entityId: string,
  ) => [
    ...featureQueryKeys.entityTypeFeatures(
      workspaceId,
      environment,
      entityType,
    ),
    entityId,
  ],
};

export const useEntityFeatures = (
  environment: Environment,
  entityId: string,
  entityType: string,
  options?: { enabled: boolean; staleTime?: number },
) => {
  const { workspace } = useWorkspaceContext();
  const featuresApi = retrieveFeaturesApi(workspace.base_url!);

  return useQuery({
    queryKey: featureQueryKeys.entityFeatures(
      workspace.id,
      environment,
      entityType,
      entityId,
    ),
    queryFn: () =>
      featuresApi.getFeatureByEntityFeaturesEnvironmentEntityTypeEntityIdGet({
        environment:
          environment as GetFeatureByEntityFeaturesEnvironmentEntityTypeEntityIdGetEnvironmentEnum,
        entityType,
        entityId,
      }),
    enabled: options?.enabled ?? (!!environment && !!entityId && !!entityType),
    staleTime: options?.staleTime,
  });
};

export const useBackfillFeatureQuery = () => {
  const { workspace } = useWorkspaceContext();
  const featureQueriesApi = featureQueriesApiCreator(workspace.base_url!);

  return useMutation({
    mutationFn: async (
      featureQuery: BackfillQueryForm & {
        env: BackfillQueryRequestEnvironmentEnum;
      },
    ) => {
      if (!featureQuery.timestamp) {
        throw new Error("Timestamp is required");
      }

      return await featureQueriesApi.backfillFeatureQueryHandlerFeatureQueriesIdBackfillPost(
        {
          id: featureQuery.id,
          backfillQueryRequest: {
            environment: featureQuery.env,
            timestamp_start: featureQuery.timestamp.from!.toISOString(),
            timestamp_end: featureQuery.timestamp.to!.toISOString(),
          },
        },
      );
    },
  });
};
